import { Button, Tooltip } from "@kyan/ui";

import { ConnectWalletWrapper } from "#/components/ConnectWalletWrapper/ConnectWalletWrapper";
import { InstrumentSummary } from "#/components/InstrumentSummary/InstrumentSummary";
import { type RFQResponseState } from "#/services/kyan/kyan.types";
import { isBuy } from "#/utils/marketActionConverter";
import { confirmTrade } from "#/utils/modals/confirmTrade";

import { PortfolioImpactAndGreeks } from "./components/PortfolioImpactAndGreeks/PortfolioImpactAndGreeks";
import { useRfqFillButtonController } from "./RfqFillButton.controller";

export interface RfqFillButtonProps {
  response: RFQResponseState;
  isTradeConfirmation?: boolean;
  onSuccess?: () => void;
}

export const RfqFillButton: React.FC<RfqFillButtonProps> = ({
  response,
  isTradeConfirmation,
  onSuccess,
}) => {
  const { isPending, handleFill, showTradeConfirmation, isDisabled, tooltip } =
    useRfqFillButtonController({
      response,
      onSuccess,
    });

  // if we move this logic into the controller, since we are using RfqFillButton we will face circular dependency issues
  const handleClick = () => {
    if (showTradeConfirmation && !isTradeConfirmation) {
      confirmTrade({
        header: "Rfq Order",
        children: (
          <div className="grid gap-4">
            {response.legs.map((leg, index) => (
              <InstrumentSummary
                key={index}
                limitPrice={leg.price}
                netPrice={leg.netPrice}
                size={leg.size.toString()}
                isBuy={isBuy(leg.direction)}
                instrumentName={leg.instrumentName}
              />
            ))}
            <PortfolioImpactAndGreeks response={response} />
          </div>
        ),

        tradeButton: ({ handleClose }) => (
          <RfqFillButton
            isTradeConfirmation
            response={response}
            onSuccess={handleClose}
          />
        ),
      });
      return;
    }

    handleFill();
  };

  return (
    <ConnectWalletWrapper
      size={isTradeConfirmation ? "lg" : "sm"}
      variant={isTradeConfirmation ? "primary" : "action"}
    >
      <Tooltip content={tooltip} disabled={!tooltip}>
        {isTradeConfirmation ? (
          <Button
            size="lg"
            disabled={isDisabled}
            isLoading={isPending}
            className="mt-2 w-full text-[15px] shadow-none"
            onClick={handleClick}
          >
            Fill order
          </Button>
        ) : (
          <Button
            size="sm"
            variant="action"
            disabled={isDisabled}
            isLoading={isPending}
            onClick={handleClick}
          >
            Fill order
          </Button>
        )}
      </Tooltip>
    </ConnectWalletWrapper>
  );
};
